import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Plus, Trash } from '@phosphor-icons/react';
import api from '../api';
import { consumeHandoff } from '../lib/workflowState';
import CustomerLocationCombobox from '../components/CustomerLocationCombobox';

// ── helpers ────────────────────────────────────────────────────────────────

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const clash = { fontFamily: 'Clash Display, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' };
const border = '1px solid hsl(0 0% 15%)';
const volt = 'hsl(75,100%,50%)';
const bg = 'hsl(0 0% 5%)';
const bg2 = 'hsl(0 0% 8%)';

function input(extra = {}) {
  return {
    background: 'hsl(0 0% 6%)',
    border: '1px solid hsl(0 0% 20%)',
    color: '#fff',
    padding: '8px 12px',
    width: '100%',
    boxSizing: 'border-box',
    ...mono,
    fontSize: 13,
    outline: 'none',
    ...extra,
  };
}

// ── StepIndicator ──────────────────────────────────────────────────────────

function StepIndicator({ step }) {
  const steps = ['Customer & Date', 'Line Items', 'Review'];
  return (
    <div style={{ display: 'flex', gap: 0, marginBottom: 32 }}>
      {steps.map((label, i) => {
        const idx = i + 1;
        const active = step === idx;
        const done = step > idx;
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 28, height: 28,
                border: `1px solid ${done || active ? volt : 'hsl(0 0% 25%)'}`,
                background: done ? volt : active ? 'hsl(0 0% 10%)' : 'transparent',
                color: done ? '#000' : active ? volt : 'hsl(0 0% 40%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, ...mono, fontWeight: 700,
              }}>
                {done ? <Check size={12} weight="bold" /> : idx}
              </div>
              <span style={{ fontSize: 11, ...mono, color: active ? volt : done ? '#fff' : 'hsl(0 0% 40%)' }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 1, background: done ? volt : 'hsl(0 0% 15%)', margin: '0 12px' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── BatchSplits ────────────────────────────────────────────────────────────

function BatchSplits({ item, onChange }) {
  if (!item.batch_tracked) return null;

  const batches = item.batches || [];

  function addBatch() {
    onChange({ ...item, batches: [...batches, { batch_no: '', qty: '' }] });
  }

  function updateBatch(idx, field, val) {
    const next = batches.map((b, i) => i === idx ? { ...b, [field]: val } : b);
    onChange({ ...item, batches: next });
  }

  function removeBatch(idx) {
    onChange({ ...item, batches: batches.filter((_, i) => i !== idx) });
  }

  const totalBatchQty = batches.reduce((s, b) => s + (parseFloat(b.qty) || 0), 0);
  const mismatch = item.quantity && totalBatchQty !== parseFloat(item.quantity);

  return (
    <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: `2px solid hsl(0 0% 20%)` }}>
      <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 6 }}>BATCH SPLITS</div>
      {batches.map((b, idx) => (
        <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
          <input
            data-testid={`batch-no-input-${idx}`}
            placeholder="Batch No"
            value={b.batch_no}
            onChange={e => updateBatch(idx, 'batch_no', e.target.value)}
            style={{ ...input(), width: 160 }}
          />
          <input
            data-testid={`batch-qty-input-${idx}`}
            type="number"
            placeholder="Qty"
            value={b.qty}
            onChange={e => updateBatch(idx, 'qty', e.target.value)}
            style={{ ...input(), width: 80 }}
          />
          <button
            data-testid={`remove-batch-btn-${idx}`}
            onClick={() => removeBatch(idx)}
            style={{ background: 'none', border: 'none', color: 'hsl(0 0% 40%)', cursor: 'pointer', padding: 4 }}
          >
            <Trash size={14} />
          </button>
        </div>
      ))}
      {mismatch && (
        <div style={{ fontSize: 10, ...mono, color: 'hsl(0 70% 60%)', marginBottom: 4 }}>
          Batch total ({totalBatchQty}) ≠ line qty ({item.quantity})
        </div>
      )}
      <button
        data-testid="add-batch-btn"
        onClick={addBatch}
        style={{ fontSize: 11, ...mono, color: volt, background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
      >
        <Plus size={12} /> Add Batch
      </button>
    </div>
  );
}

// ── Step 1: Customer & Date ─────────────────────────────────────────────────

function Step1({ header, setHeader, customers, prefillLocked, billTo, setBillTo, shipTo, setShipTo }) {
  const handleBillTo = (val) => {
    setBillTo(val);
    setHeader(h => ({
      ...h,
      customer_id: val?.customer_id || '',
      customer_name: val?.customer_name || '',
      customer_gstin: val?.location?.gstin || '',
      bill_to_location_id: val?.location_id || '',
      bill_to_location: val?.location || {},
      // reset ship-to when customer changes
      ship_to_location_id: '',
      ship_to_location: {},
    }));
    setShipTo(null);
  };

  const handleShipTo = (val) => {
    setShipTo(val);
    setHeader(h => ({
      ...h,
      ship_to_location_id: val?.location_id || '',
      ship_to_location: val?.location || {},
    }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 6 }}>BILL TO *</div>
        <CustomerLocationCombobox
          data-testid="invoice-bill-to-combobox"
          customers={customers}
          value={billTo}
          onChange={handleBillTo}
          disabled={prefillLocked}
          placeholder="Type customer or location…"
        />
      </div>
      {header.customer_id && (
        <div>
          <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 6 }}>
            SHIP TO <span style={{ color: 'hsl(0 0% 35%)' }}>(optional — leave blank if same as bill-to)</span>
          </div>
          <CustomerLocationCombobox
            data-testid="invoice-ship-to-combobox"
            customers={customers}
            scopeToCustomerId={header.customer_id}
            value={shipTo}
            onChange={handleShipTo}
            disabled={prefillLocked}
            placeholder="Select ship-to location…"
          />
        </div>
      )}
      <div>
        <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 6 }}>INVOICE DATE</div>
        <input
          data-testid="invoice-date-input"
          type="date"
          value={header.invoice_date}
          onChange={e => setHeader(h => ({ ...h, invoice_date: e.target.value }))}
          style={input()}
        />
      </div>
      <div>
        <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 6 }}>DUE DATE</div>
        <input
          data-testid="invoice-due-date-input"
          type="date"
          value={header.due_date}
          onChange={e => setHeader(h => ({ ...h, due_date: e.target.value }))}
          style={input()}
        />
      </div>
      <div>
        <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 6 }}>NOTES (OPTIONAL)</div>
        <textarea
          data-testid="invoice-notes-input"
          value={header.notes}
          onChange={e => setHeader(h => ({ ...h, notes: e.target.value }))}
          rows={3}
          style={{ ...input(), resize: 'vertical' }}
        />
      </div>
    </div>
  );
}

// ── Step 2: Line Items ──────────────────────────────────────────────────────

const BLANK_LINE = { product_id: '', product_name: '', quantity: '', unit_price: '', hsn_code: '', gst_rate: '', batch_tracked: false, batches: [] };

function Step2({ lineItems, setLineItems, products, prefillLocked }) {
  function addLine() {
    setLineItems(items => [...items, { ...BLANK_LINE }]);
  }

  function updateLine(idx, patch) {
    setLineItems(items => items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  }

  function removeLine(idx) {
    setLineItems(items => items.filter((_, i) => i !== idx));
  }

  function handleProductSelect(idx, productId) {
    const p = products.find(p => p.id === productId);
    if (p) {
      updateLine(idx, {
        product_id: p.id,
        product_name: p.name,
        hsn_code: p.hsn_code || '',
        gst_rate: p.gst_rate ?? '',
        batch_tracked: p.batch_tracked || false,
        batches: [],
      });
    } else {
      updateLine(idx, { product_id: '', product_name: '' });
    }
  }

  return (
    <div>
      {lineItems.map((item, idx) => (
        <div key={idx} style={{ border, padding: 12, marginBottom: 12, background: bg2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)' }}>LINE {idx + 1}</span>
            {!prefillLocked && lineItems.length > 1 && (
              <button
                data-testid={`remove-line-btn-${idx}`}
                onClick={() => removeLine(idx)}
                style={{ background: 'none', border: 'none', color: 'hsl(0 0% 40%)', cursor: 'pointer' }}
              >
                <Trash size={14} />
              </button>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 4 }}>PRODUCT</div>
              {prefillLocked ? (
                <div style={{ ...input(), color: '#fff' }}>{item.product_name}</div>
              ) : (
                <select
                  data-testid={`line-product-select-${idx}`}
                  value={item.product_id}
                  onChange={e => handleProductSelect(idx, e.target.value)}
                  style={input()}
                >
                  <option value="">— Select —</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 4 }}>HSN CODE</div>
              <input
                data-testid={`line-hsn-${idx}`}
                value={item.hsn_code}
                onChange={e => updateLine(idx, { hsn_code: e.target.value })}
                style={input()}
                placeholder="HSN"
              />
            </div>
            <div>
              <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 4 }}>QUANTITY</div>
              <input
                data-testid={`line-qty-${idx}`}
                type="number"
                value={item.quantity}
                onChange={e => updateLine(idx, { quantity: e.target.value })}
                style={input()}
              />
            </div>
            <div>
              <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 4 }}>UNIT PRICE (₹)</div>
              <input
                data-testid={`line-price-${idx}`}
                type="number"
                value={item.unit_price}
                onChange={e => updateLine(idx, { unit_price: e.target.value })}
                style={input()}
              />
            </div>
            <div>
              <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 4 }}>GST RATE (%)</div>
              <input
                data-testid={`line-gst-${idx}`}
                type="number"
                value={item.gst_rate}
                onChange={e => updateLine(idx, { gst_rate: e.target.value })}
                style={input()}
                placeholder="e.g. 18"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 4 }}>SUBTOTAL</div>
              <div style={{ ...input(), color: volt }}>
                ₹{((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          <BatchSplits item={item} onChange={updated => updateLine(idx, updated)} />
        </div>
      ))}

      {!prefillLocked && (
        <button
          data-testid="add-line-btn"
          onClick={addLine}
          style={{ fontSize: 12, ...mono, color: volt, background: 'none', border: `1px solid hsl(0 0% 20%)`, padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={14} /> Add Line Item
        </button>
      )}
    </div>
  );
}

// ── Step 3: Review ──────────────────────────────────────────────────────────

function Step3({ header, lineItems, customers }) {
  const customer = customers.find(c => c.id === header.customer_id);

  const subtotal = lineItems.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const totalGst = lineItems.reduce((s, it) => {
    const sub = (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0);
    return s + sub * ((parseFloat(it.gst_rate) || 0) / 100);
  }, 0);
  const total = subtotal + totalGst;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ border, padding: 16, background: bg2 }}>
        <div style={{ fontSize: 10, ...mono, color: 'hsl(0 0% 50%)', marginBottom: 8 }}>BILL TO</div>
        <div style={{ ...mono, fontSize: 14, color: '#fff' }}>{customer?.name || '—'}</div>
        <div style={{ ...mono, fontSize: 12, color: 'hsl(0 0% 50%)', marginTop: 4 }}>Due: {header.due_date || '—'}</div>
      </div>

      <div style={{ border, background: bg2 }}>
        <div style={{ padding: '8px 16px', borderBottom: border, fontSize: 10, ...mono, color: 'hsl(0 0% 50%)' }}>LINE ITEMS</div>
        {lineItems.map((item, idx) => (
          <div key={idx} style={{ padding: '10px 16px', borderBottom: idx < lineItems.length - 1 ? border : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ ...mono, fontSize: 13, color: '#fff' }}>{item.product_name}</div>
                <div style={{ ...mono, fontSize: 10, color: 'hsl(0 0% 50%)', marginTop: 2 }}>
                  {item.hsn_code && `HSN ${item.hsn_code} · `}{item.quantity} × ₹{item.unit_price} · GST {item.gst_rate || 0}%
                </div>
                {item.batches?.length > 0 && (
                  <div style={{ ...mono, fontSize: 10, color: 'hsl(0 0% 40%)', marginTop: 2 }}>
                    {item.batches.map(b => `${b.batch_no} (${b.qty})`).join(', ')}
                  </div>
                )}
              </div>
              <div style={{ ...mono, fontSize: 13, color: volt }}>
                ₹{((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ border, padding: 16, background: bg2 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ ...mono, fontSize: 12, color: 'hsl(0 0% 50%)' }}>SUBTOTAL</span>
          <span style={{ ...mono, fontSize: 12, color: '#fff' }}>₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ ...mono, fontSize: 12, color: 'hsl(0 0% 50%)' }}>GST</span>
          <span style={{ ...mono, fontSize: 12, color: '#fff' }}>₹{totalGst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
        </div>
        <div style={{ height: 1, background: 'hsl(0 0% 20%)', margin: '10px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ ...mono, fontSize: 14, color: '#fff', fontWeight: 700 }}>TOTAL</span>
          <span style={{ ...mono, fontSize: 14, color: volt, fontWeight: 700 }}>₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
        </div>
      </div>
    </div>
  );
}

// ── InvoiceCreate (main) ───────────────────────────────────────────────────

export default function InvoiceCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const actionKey = searchParams.get('action_key');
  const handoff = actionKey ? consumeHandoff(actionKey) : null;
  const fromDispatch = searchParams.get('from_dispatch') || handoff?.dispatch_id || null;

  const [step, setStep] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Combobox value objects
  const [billTo, setBillTo] = useState(null);
  const [shipTo, setShipTo] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const due = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];

  const [header, setHeader] = useState({
    customer_id: '',
    customer_name: '',
    customer_gstin: '',
    invoice_date: today,
    due_date: due,
    notes: '',
    dispatch_id: fromDispatch || null,
    bill_to_location_id: '',
    bill_to_location: {},
    ship_to_location_id: '',
    ship_to_location: {},
  });

  const [lineItems, setLineItems] = useState([{ ...BLANK_LINE }]);

  // Load customers + products
  useEffect(() => {
    api.get('/customers').then(r => setCustomers(r.data)).catch(() => {});
    api.get('/products').then(r => setProducts(r.data)).catch(() => {});
  }, []);

  // Prefill from dispatch
  useEffect(() => {
    if (!fromDispatch) return;
    setPrefillLoading(true);
    api.get(`/invoices/from-dispatch/${fromDispatch}`)
      .then(r => {
        const d = r.data;
        setHeader(h => ({
          ...h,
          customer_id: d.customer_id,
          customer_name: d.customer_name || '',
          customer_gstin: d.bill_to_location?.gstin || '',
          dispatch_id: fromDispatch,
          bill_to_location_id: d.bill_to_location_id || '',
          bill_to_location: d.bill_to_location || {},
          ship_to_location_id: d.ship_to_location_id || '',
          ship_to_location: d.ship_to_location || {},
        }));
        // Restore combobox value objects from dispatch prefill
        if (d.bill_to_location_id && d.bill_to_location) {
          setBillTo({
            customer_id: d.customer_id,
            customer_name: d.customer_name || '',
            location_id: d.bill_to_location_id,
            location_nickname: d.bill_to_location.nickname || '',
            location: d.bill_to_location,
          });
        }
        setLineItems(d.line_items.map(it => ({
          product_id: it.product_id,
          product_name: it.product_name,
          quantity: String(it.quantity),
          unit_price: String(it.unit_price),
          hsn_code: it.hsn_code || '',
          gst_rate: String(it.gst_rate || ''),
          batch_tracked: it.batch_tracked || false,
          batches: [],
        })));
      })
      .catch(() => {})
      .finally(() => setPrefillLoading(false));
  }, [fromDispatch]);

  const prefillLocked = !!fromDispatch;

  const canNext1 = header.customer_id && header.bill_to_location_id && header.due_date;

  const hasBatchMismatch = lineItems.some(item => {
    if (!item.batch_tracked) return false;
    const total = (item.batches || []).reduce((s, b) => s + (parseFloat(b.qty) || 0), 0);
    return parseFloat(item.quantity) !== total;
  });

  async function handleSubmit() {
    setSaving(true);
    try {
      const payload = {
        customer_id: header.customer_id,
        customer_name: header.customer_name,
        customer_gstin: header.customer_gstin || '',
        invoice_date: header.invoice_date,
        due_date: header.due_date,
        notes: header.notes,
        dispatch_id: header.dispatch_id,
        bill_to_location_id: header.bill_to_location_id,
        bill_to_location: header.bill_to_location,
        ship_to_location_id: header.ship_to_location_id,
        ship_to_location: header.ship_to_location,
        line_items: lineItems.map(it => ({
          product_id: it.product_id,
          product_name: it.product_name,
          quantity: parseFloat(it.quantity),
          unit_price: parseFloat(it.unit_price),
          hsn_code: it.hsn_code,
          gst_rate: parseFloat(it.gst_rate) || 0,
          batches: (it.batches || []).map(b => ({ batch_no: b.batch_no, qty: parseFloat(b.qty) || 0 })),
        })),
      };
      const res = await api.post('/invoices', payload);
      const invoiceNo = res.data?.invoice_no;
      navigate(`/finance${invoiceNo ? `?highlight=${invoiceNo}` : ''}`);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  if (prefillLoading) {
    return (
      <div style={{ padding: 32, ...mono, color: 'hsl(0 0% 40%)' }}>
        Loading dispatch data…
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'hsl(0 0% 3%)', zIndex: 100, overflowY: 'auto' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
          <button
            data-testid="invoice-back-btn"
            onClick={() => navigate('/finance')}
            style={{ background: 'none', border: 'none', color: 'hsl(0 0% 50%)', cursor: 'pointer', padding: 0 }}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 style={{ ...clash, fontSize: 20, color: '#fff', margin: 0 }}>
            {fromDispatch ? 'Invoice from Dispatch' : 'New Invoice'}
          </h1>
          {fromDispatch && (
            <span style={{ ...mono, fontSize: 10, color: 'hsl(0 0% 40%)', border: '1px solid hsl(0 0% 20%)', padding: '2px 8px' }}>
              PREFILLED
            </span>
          )}
        </div>

        <StepIndicator step={step} />

        {/* Step content */}
        <div style={{ minHeight: 320 }}>
          {step === 1 && (
            <Step1
              header={header}
              setHeader={setHeader}
              customers={customers}
              prefillLocked={prefillLocked}
              billTo={billTo}
              setBillTo={setBillTo}
              shipTo={shipTo}
              setShipTo={setShipTo}
            />
          )}
          {step === 2 && (
            <Step2
              lineItems={lineItems}
              setLineItems={setLineItems}
              products={products}
              prefillLocked={prefillLocked}
            />
          )}
          {step === 3 && (
            <Step3
              header={header}
              lineItems={lineItems}
              customers={customers}
            />
          )}
        </div>

        {/* Nav buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32, paddingTop: 16, borderTop: border }}>
          <button
            data-testid="invoice-prev-btn"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
            style={{ ...mono, fontSize: 12, color: step === 1 ? 'hsl(0 0% 30%)' : '#fff', background: 'none', border, padding: '8px 20px', cursor: step === 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <ArrowLeft size={14} /> Back
          </button>

          {step < 3 ? (
            <button
              data-testid="invoice-next-btn"
              onClick={() => setStep(s => s + 1)}
              disabled={step === 1 && !canNext1}
              style={{ ...mono, fontSize: 12, color: '#000', background: (step === 1 && !canNext1) ? 'hsl(0 0% 20%)' : volt, border: 'none', padding: '8px 20px', cursor: (step === 1 && !canNext1) ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
            >
              Next <ArrowRight size={14} />
            </button>
          ) : (
            <button
              data-testid="invoice-submit-btn"
              onClick={handleSubmit}
              disabled={saving || hasBatchMismatch}
              style={{ ...mono, fontSize: 12, color: '#000', background: (saving || hasBatchMismatch) ? 'hsl(0 0% 20%)' : volt, border: 'none', padding: '8px 24px', cursor: (saving || hasBatchMismatch) ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <Check size={14} /> {saving ? 'Saving…' : 'Create Invoice'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
